import { useIntl } from '@jetshop/intl';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuItems,
} from '@jetshop/ui/DropdownMenu';
import React, { useState } from 'react';
import { css } from 'linaria';
import { ReactComponent as Carrot } from '../../svg/Carrot.svg';
import ButtonWithLoading from '../ui/Button';
import { SectionHeading } from './ProductInfo';
import ColorBox from '../ui/ColorBox';
import FabricBox from '../ui/FabricBox';
import { theme } from '../Theme';

const buttonSelectStyles = css`
  position: relative;
  h3 {
    margin-top: 0;
  }
  .button-container {
    margin: 0 -1.25rem;
    align-items: center;
  }
  .disabled-color {
    cursor: not-allowed !important;
    * {
      cursor: not-allowed !important;
    }
  }
  button {
    border: none;
    padding: 0;
    line-height: 36px;
    font-size: 16px;
    outline: none;
    border-radius: 50%;
    height: 40px;
    width: 40px;
    display: flex;
    flex-wrap: wrap;
    margin: 0.5rem 1rem;
    align-items: center;
    justify-content: center;
    border: 0.75px solid transparent;
    background-color: transparent;
    display: inline-block;
    &[aria-pressed='true'] {
      background-color: black;
    }
  }

  .color-selection {
    display: flex;
    gap: 0.8rem;
    margin: 0 0 26px 0;
    padding: 8px 24px 8px 4px;
    flex-wrap: wrap;

    ${theme.below.lg} {
      overflow: scroll;
      flex-wrap: nowrap;
    }

    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .fabric-selection {
    display: flex;
    gap: 0.8rem;
    margin: 0 0 50px 0; /* Increased bottom margin for tooltip space */
    padding: 8px 24px 8px 4px;
    flex-wrap: wrap;
    overflow-y: visible; /* Allow tooltips to show below */

    ${theme.below.lg} {
      flex-wrap: nowrap;
      overflow-x: auto;
    }
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  /* .fabric-selection::after {
    position: absolute;
    z-index: 1;
    top: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 70%,
      rgba(255, 255, 255, 0.9) 90%,
      rgba(255, 255, 255, 1) 100%
    );
    width: 40px;
    content: '';
    pointer-events: none;
  } */

  ::after {
    position: absolute;
    z-index: 1;
    top: 0;
    left: auto;
    right: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, 1) 100%
    );
    height: 74%;
    width: 30px;
    content: '';
  }
`;

const dropdownStyles = css`
  margin-bottom: 1em;
  h3 {
    padding-bottom: 0;
  }

  [data-flight-dropdown] {
    position: relative;
    background: white;
  }
  [data-flight-dropdown-button] {
    width: 100%;
    height: 54px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #ffffff;
    border: 1px solid #dedede;
    position: relative;
    cursor: pointer;
    outline: none;
    width: 100%;
    font-size: 15px;
    padding-left: 16px;
    svg {
      margin-right: 16px;
      transition: transform 200ms;
    }
  }
  [data-flight-dropdown-open='true'] {
    [data-flight-dropdown-button] {
      svg {
        transform: rotate(-180deg);
      }
    }
  }
  [data-flight-dropdown-items] {
    width: 100%;
    margin-top: -1px;
    border: 1px solid #dedede;
    border-bottom: none;
    position: absolute;
  }
  [data-flight-dropdown-item] {
    justify-content: space-between;
    width: 100%;
    height: 54px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #dedede;
    position: relative;
    cursor: pointer;
    background-color: #ffffff;
    color: black;
    font-size: 15px;
    padding-left: 16px;

    :hover {
      background-color: #f2f2f2;
      color: black;
      outline: none;
    }
    :focus {
      outline: none;
    }
  }
  svg {
    margin-left: auto;
  }
`;

export function VariantSelector({
  product,
  variantHandler,
  selectedVariation,
  colorNestled,
}) {
  // Sort options to ensure color comes before fabric
  const sortedOptions = [...product.variants.options].sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    // Define the desired order
    const order = ['färg', 'tygnamn', 'size'];

    const aIndex = order.indexOf(aName);
    const bIndex = order.indexOf(bName);

    // If both are in the order array, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one is in the order array, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither is in the order array, maintain original order
    return 0;
  });

  return (
    <div className="variant-selector" style={{ width: '100%' }}>
      {sortedOptions.map((option) =>
        option.name.toLowerCase() === 'size' ||
        option.name.toLowerCase() === 'färg' ||
        option.name.toLowerCase() === 'tygnamn' ? (
          <ButtonSelect
            option={option}
            variantHandler={variantHandler}
            key={option.name}
            colorSelection={option.name.toLowerCase() === 'färg'}
            fabricSelection={option.name.toLowerCase() === 'tygnamn'}
            selectedVariation={selectedVariation}
            colorNestled={colorNestled}
            product={product}
          />
        ) : (
          <DropdownVariantSelect
            key={option.name}
            option={option}
            variantHandler={variantHandler}
          />
        )
      )}
    </div>
  );
}

function ButtonSelect({
  option,
  variantHandler,
  selectedVariation,
  colorSelection = false,
  fabricSelection = false,
  product,
}) {
  const { getSelectedValue, validateSelection, selectValue } = variantHandler;
  const selectedValue = getSelectedValue(option);
  const t = useIntl();

  // Get the currently selected color for fabric filtering
  const getSelectedColor = () => {
    if (!selectedVariation?.values) return null;

    const colorOption = product.variants.options.find(
      (opt) => opt.name.toLowerCase() === 'färg'
    );

    if (!colorOption) return null;

    return selectedVariation.values.find((value) =>
      colorOption.values.includes(value)
    );
  };

  // Filter available fabrics based on selected color
  const getAvailableFabrics = () => {
    const selectedColor = getSelectedColor();
    if (!selectedColor || !fabricSelection) return option.values;

    // Filter fabrics that are available for the selected color
    return option.values.filter((fabricName) => {
      return product.variants.values.some(
        (variant) =>
          variant.values.includes(fabricName) &&
          variant.values.includes(selectedColor)
      );
    });
  };

  const selectionClass = colorSelection
    ? 'color-selection'
    : fabricSelection
    ? 'fabric-selection'
    : '';

  const matchSelectedVariation = selectedVariation?.values?.find((value) =>
    option.values.includes(value)
  );

  const availableValues = fabricSelection
    ? getAvailableFabrics()
    : option.values;
  const selectedColor = getSelectedColor();

  return (
    <div className={buttonSelectStyles}>
      <SectionHeading>
        {t('Select {optionName}', {
          optionName: option.name === 'Tygnamn' ? 'Tyg' : option.name,
        })}
      </SectionHeading>
      <div className={'button-container ' + selectionClass}>
        {fabricSelection
          ? // Fabric selection logic
            availableValues.map((value) => {
              const validation = validateSelection(value, option);
              const isAvailable = validation !== 'invalid';

              return (
                <div
                  key={option.name + value}
                  style={{
                    opacity: isAvailable ? 1 : 0.8,
                    filter: isAvailable ? 'none' : 'none',
                  }}
                >
                  <FabricBox
                    fabricName={value}
                    selectedColor={selectedColor}
                    clickClosure={() =>
                      isAvailable && selectValue(value, option)
                    }
                    boxsize={'big'}
                    active={matchSelectedVariation === value && true}
                    disabled={!isAvailable}
                    showLabel={true}
                  />
                </div>
              );
            })
          : // Existing color and other selection logic
            option.values.map((value) => {
              const validation = validateSelection(value, option);
              return colorSelection ? (
                <ColorBox
                  group={value}
                  key={option.name + value}
                  clickClosure={() => selectValue(value, option)}
                  boxsize={'big'}
                  active={matchSelectedVariation === value && true}
                  showLabel={true}
                />
              ) : (
                <ButtonWithLoading
                  key={option.name + value}
                  onClick={() => selectValue(value, option)}
                  disabled={validation === 'invalid'}
                  secondary={value !== selectedValue}
                  aria-pressed={value === selectedValue}
                >
                  {value}
                </ButtonWithLoading>
              );
            })}
      </div>
    </div>
  );
}

function DropdownVariantSelect({ option, variantHandler }) {
  const { getSelectedValue, validateSelection, selectValue } = variantHandler;

  const selectedValue = getSelectedValue(option);
  return (
    <div className={dropdownStyles}>
      <SectionHeading>
        {option.name === 'Tygnamn' ? 'Välj tyg' : option.name}
      </SectionHeading>
      <DropdownMenu unstyled>
        <DropdownMenuButton id={`option-${option.name}`}>
          {selectedValue && selectedValue}
          <Carrot width="13" height="7" />
        </DropdownMenuButton>
        <DropdownMenuItems style={{ zIndex: 9999 }}>
          {option.values.map((value) => {
            const validation = validateSelection(value, option);
            return (
              <DropdownMenuItem
                key={value + option.name}
                disabled={validation === 'invalid'}
                onSelect={({ setIsOpen }) => {
                  selectValue(value, option);
                  setIsOpen(false);
                }}
                style={{ opacity: validation === 'invalid' ? 0.5 : 1 }}
              >
                {value}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuItems>
      </DropdownMenu>
    </div>
  );
}
